import React, { FC, useEffect, useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { SplashScreen } from '@capacitor/splash-screen'
import { LiveUpdate } from '@capawesome/capacitor-live-update'
import { Preferences } from '@capacitor/preferences'
import './header.css'

// COMPONENTS
import ServiceBanner from '@components/organisims/service-banner'
import UpdateBanner from '@components/organisims/update-banner'

// UTILITIES
import { flatten } from '@utils/string-helper'
import { useGlobalSettings } from '../../../../routes/_globalStore/_hooks'
import { platform } from '@utils/capacitor-helper'
import UpdateModal from '@components/organisims/update-modal'

interface Props {
    className?: string
    children?: React.ReactNode
    hidden?: boolean
}

const Input: FC<Props & { section: 'container' | 'start' | 'content' | 'end' }> = (props) => {
    const { className: classProps, section, children, hidden } = props
    const container = flatten(`column-header-${section}`, { hidden }, classProps)
    const [ready, setIsReady] = useState(false)

    useEffect(() => {
        setIsReady(true)
        var elm = document.querySelector(`column-header-${section}`)
        var innerHTML = ''
        if (elm?.hasChildNodes()) {
            innerHTML = elm.innerHTML
            elm.replaceChildren('')
        }
        elm?.setAttribute('class', container)
        return () => {
            elm = document.querySelector(`column-header-${section}`)
            elm?.replaceChildren(innerHTML)
            elm?.setAttribute('class', `column-header-${section}`)
            setIsReady(false)
        }
    }, [container])

    if (ready) switch (section) {
        case 'container': return <Container children={children} />
        case 'start': return <Start children={children} />
        case 'content': return <Content children={children} />
        case 'end': return <End children={children} />
    }
    else return null
}
const Container: FC<{ children?: React.ReactNode }> = ({ children }) => createPortal((<div className="column-header">{children}</div>), document.querySelector('header.column-header-container')!)
const Start: FC<{ children?: React.ReactNode }> = ({ children }) => {
    const root = document.querySelector('.column-header-start')
    return root ? createPortal(children, root) : null
}
const Content: FC<{ children?: React.ReactNode }> = ({ children }) => {
    const root = document.querySelector('.column-header-content')
    return root ? createPortal(children, root) : null
}
const End: FC<{ children?: React.ReactNode }> = ({ children }) => {
    const root = document.querySelector('.column-header-end')
    return root ? createPortal(children, root) : null
}

const Outlet: FC<Props & { content?: 'logo' | 'empty' }> = (props) => {
    const { className: classProps, hidden } = props
    const location = useLocation()
    const isAppleWalletExplainer = location.pathname.includes('wallet-explainer')
    const container = flatten("column-header-container", { hidden }, classProps)
    const { setShouldIgnoreUpdateAvailable, state } = useGlobalSettings()
    const { isServicable, isUpdateAvailable, shouldIgnoreUpdateAvailable } = state
    const [isBeta, setIsBeta] = useState(false)

    // Load beta preference on mount
    useEffect(() => {
        async function loadBetaPreference() {
            try {
                const { value } = await Preferences.get({ key: 'beta' })
                setIsBeta(value === 'true')
            } catch (error) {
                console.error('Error loading beta preference:', error)
            }
        }
        if (platform !== 'web') {
            loadBetaPreference()
        }
    }, [])

    const showLogo = useMemo(() => {
        const pathName = window.location.pathname
        return !['sign-in', 'verify'].includes(pathName.replace('/', ''))
    }, [window.location.pathname])

    const showUpdateAlert = useMemo(() => {
        const pathName = window.location.pathname
        return !['sign-in', 'verify'].includes(pathName.replace('/', '')) && platform !== 'web'
    }, [window.location.pathname, platform])

    return (
        <header
            className={container}
            style={{
                background: showLogo ? undefined : 'transparent'
            }}
        >
            {isAppleWalletExplainer ? (
                <div className="column-header">
                    <div className="column-header-start" style={{ flex: 2 }} />
                    <div className="column-header-end" />
                </div>
            ) : (
                <div className="column-header">
                    <div className="column-header-start" />
                    <div className="column-header-content">
                        {showLogo && !isBeta &&
                            <Link to="/account">
                                <picture className="splitcard-logo">
                                    <source type="image/png" srcSet="/splitcard/logo/black-and-blue-fallback.png" />
                                    <img src="/splitcard/logo/black-and-blue-fallback.png" alt="Splitcard Logo" />
                                </picture>
                            </Link>
                        }
                        {showLogo && isBeta &&
                            <Link to="/account">
                                <picture className="splitcard-logo-beta">
                                    <source type="image/png" srcSet="/splitcard/logo/beta.png" />
                                    <img src="/splitcard/logo/beta.png" alt="Splitcard Logo" />
                                </picture>
                            </Link>
                        }
                    </div>
                    <div className="column-header-end" />
                </div>
            )}
            <ServiceBanner open={!isServicable} />
            {showUpdateAlert && <>
                {(!shouldIgnoreUpdateAvailable && !isUpdateAvailable.isCritical) &&
                    <UpdateBanner
                        open={isUpdateAvailable.isAvailable && !isUpdateAvailable.isCritical}
                        update={async () => {
                            console.log('[ LiveUpdate ] [ resume ] reload()')
                            await SplashScreen.show()
                            await LiveUpdate.reload()
                        }}
                        ignore={() => setShouldIgnoreUpdateAvailable(true)}
                    />
                }
                {isUpdateAvailable.isCritical &&
                    <UpdateModal
                        update={async () => {
                            console.log('[ LiveUpdate ] [ resume ] reload()')
                            await SplashScreen.show()
                            await LiveUpdate.reload()
                        }}
                    />
                }
            </>}
        </header>
    )
}

export const ColumnHeader = {
    Input: Object.assign(Input, {
        Container: (props: Props) => <Input section="container" {...props} />,
        Start: (props: Props) => <Input section="start" {...props} />,
        Content: (props: Props) => <Input section="content" {...props} />,
        End: (props: Props) => <Input section="end" {...props} />,
    }),
    Outlet,
}
